import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

// API/Components
import { DATE_RANGES } from '../components/enums';
import axiosInstance from '../components/axios';
import useLocationGraphConfig from '../components/hooks/useLocationGraphConfig';
import useHotkeyHandlers from '../components/hooks/useHotkeyHandlers';
import DataViewSwitch from '../components/data_view_switch';
import SystemTrendReport from '../components/system_trend_report';
import SystemTrendGraph from '../components/system_trend_graph';

// Material UI
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';


// System Trend sub-page rendering (Location Name, Title)
export default function SystemTrend() {
	const { locationNum } = useParams();
	const [ searchParams ] = useSearchParams();

	// State Management
	const [selectedDateRange, setSelectedDateRange] = useState(DATE_RANGES[0].value);

	const [trendData, setTrendData] = useState({});
	const { locationInfo, limitData, graphScalesData, isLoading, setIsLoading } = useLocationGraphConfig({
		locationNum,
		limitsEndpoint: `/loc/${locationNum}/limits`,
		scalesEndpoint: `/loc/${locationNum}/graph_scales`
	});

	const [graphDisplay, setGraphDisplay] = useState(false);
	const [graphType, setGraphType] = useState(0);
	
	// Check if current measurement is disabled from limits data
	const noCurrentMeasurement = limitData?.no_current_measurement ?? false;
	
	const graphTypeRef = useRef();

	// Helpers to check if trendData is empty and if DataViewSwitch should be disabled
	const isTrendDataEmpty = !trendData || (Object.keys(trendData).length === 0);
	const isDataSwitchDisabled = isLoading || isTrendDataEmpty;
	const dataSwitchDisabledText = isTrendDataEmpty ? "No data available to graph" : "Data is loading...";

	// Handle the current graphType for hotkey nav
	useEffect(() => {
		graphTypeRef.current = graphType;
	}, [graphType]);

	// Handle keypresses for graph navigation
	useHotkeyHandlers([
		{
			key: 'F8',
			down: (e) => {
				e.preventDefault();
				switch(graphTypeRef.current) {
					case 0:  // Voltage -> Current (or Temperature if Current disabled)
						setGraphType(noCurrentMeasurement ? 2 : 1);
						break;
					case 1:  // Current -> Temperature
						setGraphType(2);
						break;
					case 2:  // Temperature -> Voltage
						setGraphType(0);
						break;
					default:
						setGraphType(0);
						break;
				}
			},
			up: () => {},
		},
	]);
	
	// Get the list of trend data for the specified location id
	useEffect(() => {
		setIsLoading(true);

		// Get System Trend Data
		axiosInstance.get(`/loc/${locationNum}/meas?filter=${selectedDateRange}`)
			.then(response => {
				setTrendData(response.data);
			})
			.catch(error => {
				console.error("Error fetching trend data:", error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [locationNum, selectedDateRange]);

	// Event Handlers
	const handleGraphTypeChange = (event) => {
		setGraphType(Number(event.target.value));
	}
	
	const handleDateRangeChange = (event) => {
		setSelectedDateRange(event.target.value);
	};
	
	const handleViewChange = (event) => {
		setGraphDisplay(event.target.checked);
	}
	
	return (
		<Paper
			padding='true'
			sx={{
				overflow: 'auto',
				boxShadow: '0',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Typography
				variant='h5'
				fontFamily='Saira, sans-serif'
				fontWeight='600'
				color='#0F1820'
				align='center'
				padding='5px'
			>
				{ locationInfo.location_name } (SN {locationInfo.serial_number})
			</Typography>
			<Toolbar>
				<Grid container spacing={1} alignItems="center">
					<Grid item xs alignItems="left">
						<Typography
							align="left"
							fontFamily='Saira,sans-serif'
							fontWeight='600'
							sx={{ flexGrow: 1}}
						>
							System Trend
						</Typography>
					</Grid>
					{graphDisplay &&
						<Grid item>
							<FormControl sx={{ m: 1, minWidth: 100 }}>
								<label id="graph-type-select" style={{fontFamily:"Saira, sans-serif"}}>Graph Type</label>
								<select
									id="graph-type-select"
									value={graphType}
									label="Graph Type"
									onChange={handleGraphTypeChange}
								>
									<option key={0} value={0}>Voltage</option>
									{!noCurrentMeasurement && <option key={1} value={1}>Current</option>}
									<option key={2} value={2}>Temperature</option>
								</select>
							</FormControl>
						</Grid>
					}
					<Grid item>
						<FormControl sx={{ m: 0, minWidth: 80 }}>
							<label id="range-select" style={{fontFamily:"Saira, sans-serif"}}>Date Range</label>
							<select
								id="range-select"
								value={selectedDateRange}
								label="Date Range"
								onChange={handleDateRangeChange}
								sx= {{ maxHeight: 500 }}
							>
								{ DATE_RANGES.map((range) => (
									<option key={range.value} value={range.value}>{range.name}</option>
								)) }
							</select>
						</FormControl>
					</Grid>
					<Grid item>
						<DataViewSwitch
							checked={graphDisplay}
							onChange={handleViewChange}
							leftText="Report"
							rightText="Graph"
							disabled={isDataSwitchDisabled}
							disabledText={dataSwitchDisabledText}
						/>
					</Grid>
				</Grid>
			</Toolbar>
			{ graphDisplay ?
				<SystemTrendGraph
					trendData={trendData}
					limitData={limitData}
					graphScalesData={graphScalesData}
					graphType={graphType}
					loading={isLoading}
					noCurrentMeasurement={noCurrentMeasurement}
				/>
			:
				<SystemTrendReport
					trendData={trendData}
					loading={isLoading}
					noCurrentMeasurement={noCurrentMeasurement}
				/>
			}
		</Paper>
	);
}